import React, { useState, useEffect, useMemo } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { financialService, Contract, ContractCreateData, ContractType as ContractTypeEnum } from '../../../src/services/financial.service';
import { Project } from '../../../src/services/project.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useAuth } from '../../../src/hooks/useAuth';
import { useContracts } from '../../../src/hooks/queries/use-contracts';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useAPInvoices } from '../../../src/hooks/queries/use-ap-invoices';
import { useAppNav } from '../../../src/contexts/app-nav.context';
import { pdfUtils } from '../../../src/utils/pdf.utils';
import { formatCurrency } from '../../../src/utils/currency';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Separator } from '../../ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { 
  FileText, 
  Plus,
  Search,
  Eye,
  Download,
  TrendingUp,
  DollarSign,
  CheckCircle,
  Calendar,
  Receipt,
  Users,
  Building,
  Save,
  X,
  Edit,
  Upload,
  Trash2,
  MoreVertical,
  Paperclip,
  RefreshCw
} from 'lucide-react';

// Contract Form Component
function ContractFormModal({ mode, contract, trigger, isSubContract = false, projects = [], orgId = '', onSuccess, open: controlledOpen, onOpenChange }: {
  mode: 'add' | 'edit' | 'view';
  contract?: Contract;
  trigger?: React.ReactNode;
  isSubContract?: boolean;
  projects?: Project[];
  orgId?: string;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isReadOnly = mode === 'view';
  const orgCurrency = useOrgCurrency();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (val: boolean) => {
    if (onOpenChange) onOpenChange(val);
    else setInternalOpen(val);
  };
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contractNumber: '',
    contractTitle: '',
    projectId: '',
    contractorName: '',
    clientName: '',
    contractType: 'Main Contract' as ContractTypeEnum,
    startDate: '',
    endDate: '',
    contractValue: '',
    scopeOfWork: '',
    termsAndConditions: '',
    retentionPercentage: '',
    paymentTerms: '',
    warrantyPeriodDays: '',
    budgetLines: [] as { id: number; category: string; description: string; budgetAmount: string; actualAmount: number; variance: number; notes: string; costCode: string }[],
    deliverables: [] as { id: number; title: string; description: string; dueDate: string; amount: string; status: string; priority: string; assignee: string }[],
    invoiceSchedule: [] as { id: number; milestone: string; percentage: string; amount: string; dueDate: string; status: string; invoiceNumber: string; paymentDate: string }[],
    attachments: [] as { id: number; name: string; type: string; size: string; uploadDate: string; uploadedBy: string }[],
  });

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      setFormData({
        contractNumber: contract?.contract_number || '',
        contractTitle: contract?.contract_title || '',
        projectId: contract?.project_id || '',
        contractorName: contract?.contractor_name || '',
        clientName: contract?.client_name || '',
        contractType: (contract?.contract_type || 'Main Contract') as ContractTypeEnum,
        startDate: contract?.start_date || '',
        endDate: contract?.end_date || '',
        contractValue: contract?.contract_value?.toString() || '',
        scopeOfWork: contract?.scope_of_work || '',
        termsAndConditions: contract?.terms_and_conditions || '',
        retentionPercentage: contract?.retention_percentage?.toString() || '',
        paymentTerms: contract?.payment_terms || '',
        warrantyPeriodDays: contract?.warranty_period_days?.toString() || '',
        budgetLines: [],
        deliverables: [],
        invoiceSchedule: [],
        attachments: [],
      });
    }
  }, [open, contract]);

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const contractTypes: ContractTypeEnum[] = [
    'Main Contract',
    'Subcontract',
    'Supply Agreement',
    'Service Agreement',
    'Consulting',
    'Equipment Rental',
  ];

  const budgetCategories = [
    'Site Preparation',
    'Foundation',
    'Structural Work',
    'Concrete Works',
    'Steel Works',
    'Masonry',
    'Roofing',
    'MEP Works',
    'Finishes',
    'Landscaping',
    'General Conditions',
    'Overhead & Profit'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.contractTitle.trim()) newErrors.contractTitle = 'Contract title is required';
    if (!formData.projectId) newErrors.projectId = 'Project is required';
    if (!formData.contractValue || parseFloat(formData.contractValue) <= 0) newErrors.contractValue = 'Valid contract value is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload: ContractCreateData = {
        organization_id: orgId,
        project_id: formData.projectId,
        contract_number: formData.contractNumber || `CNT-${Date.now()}`,
        contract_title: formData.contractTitle,
        contract_type: formData.contractType,
        contract_value: parseFloat(formData.contractValue),
        client_name: formData.clientName || undefined,
        contractor_name: formData.contractorName || undefined,
        payment_terms: formData.paymentTerms || undefined,
        retention_percentage: formData.retentionPercentage ? parseFloat(formData.retentionPercentage) : undefined,
        start_date: formData.startDate || undefined,
        end_date: formData.endDate || undefined,
        scope_of_work: formData.scopeOfWork || undefined,
        terms_and_conditions: formData.termsAndConditions || undefined,
        warranty_period_days: formData.warrantyPeriodDays ? parseInt(formData.warrantyPeriodDays) : undefined,
      };

      if (mode === 'add') {
        await financialService.createContract(payload);
        showSuccess('Contract created successfully');
      } else if (mode === 'edit' && contract) {
        await financialService.updateContract(contract.contract_id, payload);
        showSuccess('Contract updated successfully');
      }
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      showError({ title: 'Save failed', message: err.message || 'Please try again' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addBudgetLine = () => {
    const newId = Math.max(...formData.budgetLines.map(b => b.id), 0) + 1;
    setFormData({
      ...formData,
      budgetLines: [...formData.budgetLines, {
        id: newId,
        category: '',
        description: '',
        budgetAmount: '',
        actualAmount: 0,
        variance: 0,
        notes: '',
        costCode: ''
      }]
    });
  };

  const removeBudgetLine = (id: number) => {
    setFormData({
      ...formData,
      budgetLines: formData.budgetLines.filter(b => b.id !== id)
    });
  };

  const updateBudgetLine = (id: number, field: string, value: string | number) => {
    setFormData({
      ...formData,
      budgetLines: formData.budgetLines.map(b => 
        b.id === id ? { ...b, [field]: value, variance: field === 'budgetAmount' || field === 'actualAmount' ? 
          parseFloat(b.budgetAmount || '0') - parseFloat(b.actualAmount?.toString() || '0') : b.variance } : b
      )
    });
  };

  const addDeliverable = () => {
    const newId = Math.max(...formData.deliverables.map(d => d.id), 0) + 1;
    setFormData({
      ...formData,
      deliverables: [...formData.deliverables, {
        id: newId,
        title: '',
        description: '',
        dueDate: '',
        amount: '',
        status: 'Pending',
        priority: 'Medium',
        assignee: ''
      }]
    });
  };

  const removeDeliverable = (id: number) => {
    setFormData({
      ...formData,
      deliverables: formData.deliverables.filter(d => d.id !== id)
    });
  };

  const updateDeliverable = (id: number, field: string, value: string) => {
    setFormData({
      ...formData,
      deliverables: formData.deliverables.map(d => 
        d.id === id ? { ...d, [field]: value } : d
      )
    });
  };

  const addInvoiceSchedule = () => {
    const newId = Math.max(...formData.invoiceSchedule.map(i => i.id), 0) + 1;
    setFormData({
      ...formData,
      invoiceSchedule: [...formData.invoiceSchedule, {
        id: newId,
        milestone: '',
        percentage: '',
        amount: '',
        dueDate: '',
        status: 'Pending',
        invoiceNumber: '',
        paymentDate: ''
      }]
    });
  };

  const removeInvoiceSchedule = (id: number) => {
    setFormData({
      ...formData,
      invoiceSchedule: formData.invoiceSchedule.filter(i => i.id !== id)
    });
  };

  const updateInvoiceSchedule = (id: number, field: string, value: string) => {
    setFormData({
      ...formData,
      invoiceSchedule: formData.invoiceSchedule.map(i => 
        i.id === id ? { ...i, [field]: value } : i
      )
    });
  };

  // Comprehensive modal content with tabbed interface
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[1400px] max-h-[95vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isSubContract ? 'Sub-Contract' : 'Contract'} - {mode === 'add' ? 'New' : mode === 'edit' ? 'Edit' : 'View'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? `Create a new ${isSubContract ? 'sub-contract' : 'contract'} with project linking and budget management` : 
             mode === 'edit' ? `Update ${isSubContract ? 'sub-contract' : 'contract'} information and terms` : 
             `View detailed ${isSubContract ? 'sub-contract' : 'contract'} information`}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="budget">Budget Lines</TabsTrigger>
            <TabsTrigger value="deliverables">Deliverables</TabsTrigger>
            <TabsTrigger value="invoicing">Invoice Schedule</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Contract Information
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="contract-title">Contract Title *</Label>
                  <Input
                    id="contract-title"
                    value={formData.contractTitle}
                    onChange={(e) => setFormData({...formData, contractTitle: e.target.value})}
                    placeholder="Enter contract title"
                    readOnly={isReadOnly}
                  />
                  {errors.contractTitle && <p className="text-sm text-destructive">{errors.contractTitle}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract-number">Contract Number</Label>
                  <Input
                    id="contract-number"
                    value={formData.contractNumber}
                    onChange={(e) => setFormData({...formData, contractNumber: e.target.value})}
                    placeholder="Auto-generated if empty"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project">Project *</Label>
                  <Select value={formData.projectId} onValueChange={(value) => setFormData({...formData, projectId: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.projectId && <p className="text-sm text-destructive">{errors.projectId}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract-type">Contract Type</Label>
                  <Select value={formData.contractType} onValueChange={(value) => setFormData({...formData, contractType: value as ContractTypeEnum})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contractTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scope-of-work">Scope of Work</Label>
                  <Textarea
                    id="scope-of-work"
                    value={formData.scopeOfWork}
                    onChange={(e) => setFormData({...formData, scopeOfWork: e.target.value})}
                    placeholder="Describe the scope of work"
                    rows={4}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Parties & Terms
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="client">Client</Label>
                  <Input
                    id="client"
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    placeholder="Enter client name"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contractor">Contractor</Label>
                  <Input
                    id="contractor"
                    value={formData.contractorName}
                    onChange={(e) => setFormData({...formData, contractorName: e.target.value})}
                    placeholder="Enter contractor name"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract-value">Contract Value ({orgCurrency}) *</Label>
                  <Input
                    id="contract-value"
                    type="number"
                    value={formData.contractValue}
                    onChange={(e) => setFormData({...formData, contractValue: e.target.value})}
                    placeholder="Enter contract value"
                    readOnly={isReadOnly}
                  />
                  {errors.contractValue && <p className="text-sm text-destructive">{errors.contractValue}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="retention">Retention %</Label>
                    <Input
                      id="retention"
                      type="number"
                      value={formData.retentionPercentage}
                      onChange={(e) => setFormData({...formData, retentionPercentage: e.target.value})}
                      placeholder="10"
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment-terms">Payment Terms</Label>
                    <Input
                      id="payment-terms"
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                      placeholder="e.g. Net 30"
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="warranty-period">Warranty Period (days)</Label>
                  <Input
                    id="warranty-period"
                    type="number"
                    value={formData.warrantyPeriodDays}
                    onChange={(e) => setFormData({...formData, warrantyPeriodDays: e.target.value})}
                    placeholder="365"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="terms">Terms & Conditions</Label>
                  <Textarea
                    id="terms"
                    value={formData.termsAndConditions}
                    onChange={(e) => setFormData({...formData, termsAndConditions: e.target.value})}
                    placeholder="Enter terms and conditions"
                    rows={4}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="budget" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Budget Lines
              </h3>
              {!isReadOnly && (
                <Button onClick={addBudgetLine} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Budget Line
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Cost Code</TableHead>
                    <TableHead>Budget Amount</TableHead>
                    <TableHead>Actual Amount</TableHead>
                    <TableHead>Variance</TableHead>
                    <TableHead>Notes</TableHead>
                    {!isReadOnly && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.budgetLines.map(line => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <Select 
                          value={line.category} 
                          onValueChange={(value) => updateBudgetLine(line.id, 'category', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {budgetCategories.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={line.description}
                          onChange={(e) => updateBudgetLine(line.id, 'description', e.target.value)}
                          placeholder="Enter description"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={line.costCode}
                          onChange={(e) => updateBudgetLine(line.id, 'costCode', e.target.value)}
                          placeholder="Cost code"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={line.budgetAmount}
                          onChange={(e) => updateBudgetLine(line.id, 'budgetAmount', e.target.value)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={line.actualAmount}
                          onChange={(e) => updateBudgetLine(line.id, 'actualAmount', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <span className={`font-medium ${
                          line.variance < 0 ? 'text-red-600' : line.variance > 0 ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          {formatCurrency(line.variance || 0, orgCurrency)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={line.notes}
                          onChange={(e) => updateBudgetLine(line.id, 'notes', e.target.value)}
                          placeholder="Notes"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => removeBudgetLine(line.id)}
                            disabled={formData.budgetLines.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="deliverables" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Deliverables
              </h3>
              {!isReadOnly && (
                <Button onClick={addDeliverable} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Deliverable
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead>Status</TableHead>
                    {!isReadOnly && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.deliverables.map(deliverable => (
                    <TableRow key={deliverable.id}>
                      <TableCell>
                        <Input 
                          value={deliverable.title}
                          onChange={(e) => updateDeliverable(deliverable.id, 'title', e.target.value)}
                          placeholder="Enter title"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={deliverable.description}
                          onChange={(e) => updateDeliverable(deliverable.id, 'description', e.target.value)}
                          placeholder="Enter description"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={deliverable.dueDate}
                          onChange={(e) => updateDeliverable(deliverable.id, 'dueDate', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={deliverable.amount}
                          onChange={(e) => updateDeliverable(deliverable.id, 'amount', e.target.value)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={deliverable.priority} 
                          onValueChange={(value) => updateDeliverable(deliverable.id, 'priority', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="High">High</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="Low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={deliverable.assignee}
                          onChange={(e) => updateDeliverable(deliverable.id, 'assignee', e.target.value)}
                          placeholder="Assignee"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={deliverable.status} 
                          onValueChange={(value) => updateDeliverable(deliverable.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Delayed">Delayed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => removeDeliverable(deliverable.id)}
                            disabled={formData.deliverables.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="invoicing" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoice Schedule
              </h3>
              {!isReadOnly && (
                <Button onClick={addInvoiceSchedule} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Milestone
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Milestone</TableHead>
                    <TableHead>Percentage</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead>Status</TableHead>
                    {!isReadOnly && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.invoiceSchedule.map(invoice => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <Input 
                          value={invoice.milestone}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'milestone', e.target.value)}
                          placeholder="Enter milestone"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={invoice.percentage}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'percentage', e.target.value)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={invoice.amount}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'amount', e.target.value)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={invoice.dueDate}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'dueDate', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={invoice.invoiceNumber}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'invoiceNumber', e.target.value)}
                          placeholder="Invoice number"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={invoice.paymentDate}
                          onChange={(e) => updateInvoiceSchedule(invoice.id, 'paymentDate', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={invoice.status} 
                          onValueChange={(value) => updateInvoiceSchedule(invoice.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Invoiced">Invoiced</SelectItem>
                            <SelectItem value="Paid">Paid</SelectItem>
                            <SelectItem value="Overdue">Overdue</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => removeInvoiceSchedule(invoice.id)}
                            disabled={formData.invoiceSchedule.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Contract Documents
              </h3>
              {!isReadOnly && (
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.attachments.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type}</Badge>
                      </TableCell>
                      <TableCell>{doc.size}</TableCell>
                      <TableCell>{doc.uploadDate}</TableCell>
                      <TableCell>{doc.uploadedBy}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          {!isReadOnly && (
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {formData.attachments.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            <X className="h-4 w-4 mr-2" />
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {mode === 'add' ? 'Create Contract' : 'Update Contract'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Contracts() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const { orgId } = useAuth();
  const { navigate, routeFilters } = useAppNav();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [contractTypeFilter, setContractTypeFilter] = useState('all');
  const [viewingContract, setViewingContract] = useState<Contract | null>(null);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [deletingContractId, setDeletingContractId] = useState<string | null>(null);

  const { data: rawContracts = [], isLoading: loading, isError, refetch } = useContracts(orgId);
  const contracts = rawContracts as Contract[];
  const { data: rawProjects = [] } = useProjects(orgId);
  const projects = rawProjects as Project[];
  const { data: apInvoices = [] } = useAPInvoices(orgId);

  // Apply incoming route filters on first render
  useEffect(() => {
    if (routeFilters.contract_id) {
      // pre-filter by searching the contract_id
    }
  }, []); // intentional empty dep

  const contractStats = useMemo(() => {
    return contracts.reduce((acc, c) => {
      const linked = (apInvoices as any[]).filter(i => i.contract_id === c.contract_id);
      acc[c.contract_id] = {
        totalInvoiced: linked.reduce((s, i) => s + (i.total_amount ?? i.amount ?? 0), 0),
        invoiceCount: linked.length,
      };
      return acc;
    }, {} as Record<string, { totalInvoiced: number; invoiceCount: number }>);
  }, [contracts, apInvoices]);

  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const projectName = projects.find(p => p.project_id === contract.project_id)?.project_name || '';
      const matchesSearch =
        (contract.contract_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contract.contract_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contract.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contract.contractor_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || contract.status.toLowerCase() === statusFilter.toLowerCase();
      const matchesProject = projectFilter === 'all' || contract.project_id === projectFilter;
      const matchesType = contractTypeFilter === 'all' || contract.contract_type === contractTypeFilter;
      return matchesSearch && matchesStatus && matchesProject && matchesType;
    });
  }, [searchTerm, statusFilter, projectFilter, contractTypeFilter, contracts, projects]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
      case 'Under Review':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Under Review</Badge>;
      case 'Completed':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Completed</Badge>;
      case 'Terminated':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Terminated</Badge>;
      case 'Suspended':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Suspended</Badge>;
      case 'Draft':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Draft</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleDeleteContract = async (contractId: string) => {
    try {
      await financialService.deleteContract(contractId);
      showSuccess('Contract deleted');
      setDeletingContractId(null);
      refetch();
    } catch {
      showError('Failed to delete contract');
      setDeletingContractId(null);
    }
  };

  const handleExport = async () => {
    try {
      await pdfUtils.createSimplePDF(
        { title: 'Contract Report', subtitle: `${contracts.length} contracts`, date: new Date().toLocaleDateString() },
        contracts.map(c => ({
          title: `${c.contract_number} — ${c.contract_title}`,
          content: [
            { label: 'Status', value: c.status },
            { label: 'Value', value: c.contract_value != null ? formatCurrency(c.contract_value, orgCurrency) : '—' },
            { label: 'Type', value: c.contract_type },
            { label: 'Start Date', value: c.start_date || '—' },
            { label: 'End Date', value: c.end_date || '—' },
          ],
        })),
        `contracts-${new Date().toISOString().split('T')[0]}.pdf`
      );
    } catch {
      showError('PDF export failed');
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setProjectFilter('all');
    setContractTypeFilter('all');
  };

  const activeCount = contracts.filter(c => c.status === 'Active').length;
  const totalValue = contracts.reduce((sum, c) => sum + (c.contract_value || 0), 0);
  const contractTypeOptions = [...new Set(contracts.map(c => c.contract_type))].filter(Boolean) as string[];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Contract Management</h2>
          <p className="text-muted-foreground">Manage contracts, sub-contracts, and track performance with comprehensive documentation</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite('/contracts') && (
            <ContractFormModal
              mode="add"
              orgId={orgId ?? ''}
              projects={projects}
              onSuccess={() => refetch()}
              trigger={
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Contract
                </Button>
              }
            />
          )}
          <Button variant="outline" onClick={handleExport} disabled={contracts.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Active Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{loading ? '—' : activeCount}</div>
            <p className="text-xs text-blue-600 mt-1">Currently executing</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {loading ? '—' : `${orgCurrency} ${(totalValue / 1000000).toFixed(1)}M`}
            </div>
            <p className="text-xs text-green-600 mt-1">All contracts</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-orange-600" />
              Total Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">{loading ? '—' : contracts.length}</div>
            <p className="text-xs text-orange-600 mt-1">Across all projects</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-600" />
              Signed Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {loading ? '—' : contracts.filter(c => c.is_signed).length}
            </div>
            <p className="text-xs text-purple-600 mt-1">Executed agreements</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Under Review">Under Review</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Terminated">Terminated</SelectItem>
            <SelectItem value="Suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.project_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={contractTypeFilter} onValueChange={setContractTypeFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {contractTypeOptions.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={resetFilters}>
          <RefreshCw className="h-4 w-4 mr-1" />Reset
        </Button>
      </div>

      {/* Contracts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Contract Directory
          </CardTitle>
          <CardDescription>
            Comprehensive contract management with full lifecycle tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contract Details</TableHead>
                  <TableHead>Project & Parties</TableHead>
                  <TableHead>Financial</TableHead>
                  <TableHead>Signature</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                      <p className="text-muted-foreground text-sm">Loading contracts...</p>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredContracts.map(contract => {
                  const projectName = projects.find(p => p.project_id === contract.project_id)?.project_name || '—';
                  return (
                    <TableRow key={contract.contract_id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{contract.contract_number}</p>
                          <p className="text-sm">{contract.contract_title}</p>
                          <Badge variant="outline" className="text-xs">{contract.contract_type}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Building className="h-3 w-3" />
                            <span className="font-medium">{projectName}</span>
                          </div>
                          {contract.client_name && (
                            <div className="flex items-center gap-2">
                              <Users className="h-3 w-3" />
                              <span>{contract.client_name}</span>
                            </div>
                          )}
                          {contract.contractor_name && (
                            <div className="text-muted-foreground">{contract.contractor_name}</div>
                          )}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>{contract.start_date || '—'} – {contract.end_date || '—'}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          <span className="font-medium">{contract.contract_value != null ? formatCurrency(contract.contract_value, orgCurrency) : '—'}</span>
                          {contract.retention_percentage && (
                            <div className="text-xs text-muted-foreground">{contract.retention_percentage}% retention</div>
                          )}
                          {contract.payment_terms && (
                            <div className="text-xs text-muted-foreground">Payment: {contract.payment_terms}</div>
                          )}
                          {(contractStats[contract.contract_id]?.invoiceCount ?? 0) > 0 && (
                            <div className="text-xs text-blue-600 mt-1">
                              Invoiced: {formatCurrency(contractStats[contract.contract_id].totalInvoiced, orgCurrency)}
                              {' '}({contractStats[contract.contract_id].invoiceCount} inv.)
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm text-muted-foreground">
                          {contract.is_signed
                            ? <span className="text-green-600 font-medium">Signed</span>
                            : <span className="text-yellow-600">Unsigned</span>
                          }
                          {contract.signed_date && <div className="text-xs">{contract.signed_date}</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(contract.status)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onSelect={() => setViewingContract(contract)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {canWrite('/contracts') && (
                              <DropdownMenuItem onSelect={() => setEditingContract(contract)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Contract
                              </DropdownMenuItem>
                            )}
                            <Separator />
                            <DropdownMenuItem onSelect={() => navigate('/invoices-ap', { contract_id: contract.contract_id })}>
                              <Receipt className="h-4 w-4 mr-2" />
                              View Invoices
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => navigate('/payment-applications-ar', { contract_id: contract.contract_id })}>
                              <DollarSign className="h-4 w-4 mr-2" />
                              View Payment Apps
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => navigate('/change-orders', { contract_id: contract.contract_id })}>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              View Change Orders
                            </DropdownMenuItem>
                            <Separator />
                            {canDelete('/contracts') && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onSelect={() => setDeletingContractId(contract.contract_id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {!loading && filteredContracts.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{isError ? 'Failed to load contracts' : 'No contracts found'}</p>
                <Button variant="outline" size="sm" onClick={isError ? () => refetch() : resetFilters} className="mt-2">
                  {isError ? 'Retry' : 'Clear Filters'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {viewingContract && (
        <ContractFormModal
          mode="view"
          contract={viewingContract}
          projects={projects}
          orgId={orgId ?? ''}
          open={true}
          onOpenChange={(o) => { if (!o) setViewingContract(null); }}
        />
      )}
      {editingContract && (
        <ContractFormModal
          mode="edit"
          contract={editingContract}
          projects={projects}
          orgId={orgId ?? ''}
          onSuccess={() => { refetch(); setEditingContract(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingContract(null); }}
        />
      )}
      {deletingContractId && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) setDeletingContractId(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Contract</DialogTitle>
              <DialogDescription>This action cannot be undone. The contract will be permanently deleted.</DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeletingContractId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => handleDeleteContract(deletingContractId)}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}